import { convertNestedEmptyToNull, createCampaignUpdate, generateNewDocId, getPitchesByCampaignId, getTalentById } from "../../services/firebase";
import { asIdList } from "../../shared/asIdList";
import type { BrowseFilter, Campaign, CampaignType, Currency, LeadSource, Pitch, PitchingEligibility, Platform, Talent, User } from "../../shared/types";
import { getBrowseFilterFromEligibility } from "../../utils/browseFilter";
import { createdByRoleFromUser } from "../../utils/campaignPermissions";
import { removeParamsFromURL } from "../../utils/core";
import { normalizeEligibility, talentMatchesPitchingEligibility } from "../../utils/pitching/eligibility";

export type CampaignFormMode = "create" | "edit";

export type CampaignFormValues = {
    campaignName: string;
    details: string;
    deliverables: string;
    soundUrl: string;
    minPricePerCreator?: number;
    maxPricePerCreator?: number;
    file?: FileList;
    boostInterest: boolean;
    leadSource?: LeadSource[];
    crmLeadId?: string;
    salesTeamId?: string[];
};

function storedPrice(value: number | undefined): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stripCampaignWriteExtras<T extends object>(value: T) {
    const next = { ...value } as T & { bookings?: unknown; docMeta?: unknown; file?: unknown };
    delete next.bookings;
    delete next.docMeta;
    delete next.file;
    return next;
}

// Derives the airtableSalesTeamId list from the salesTeamId values in the form data...
function airtableSalesTeamIdForSave(salesTeamId: string[], salesTeam: User[]) {
    return asIdList(salesTeamId).flatMap((id) => {
        const airtableId = salesTeam.find((person) => person.id === id)?.managerAirtableId;
        return airtableId ? [airtableId] : [];
    });
}

export function emptyFormValues(): CampaignFormValues {
    return {
        campaignName: "",
        details: "",
        deliverables: "",
        soundUrl: "",
        minPricePerCreator: undefined,
        maxPricePerCreator: undefined,
        boostInterest: false,
    };
}

export function canSendBookerPitchDigest(createdById?: string, bookerId?: string) {
    return Boolean(createdById && bookerId && createdById !== bookerId);
}

export function applyBookerCurrency(
    booker: User,
    setCurrency: (currency: Currency) => void,
    setLocked: (locked: boolean) => void,
) {
    if (booker.clientWideSettings?.currency) {
        setCurrency(booker.clientWideSettings.currency);
        setLocked(true);
        return;
    }
    setLocked(false);
    setCurrency(booker.currency || "USD");
}


export function applyCampaignToForm(
    loaded: Campaign,
    reset: (values: CampaignFormValues) => void,
    setters: {
        setBooker: (booker: User | undefined) => void;
        setCurrency: (currency: Currency | undefined) => void;
        setCurrencyLockedFromBooker: (locked: boolean) => void;
        setCampaignType: (type: CampaignType) => void;
        setPlatforms: (platforms: Platform[]) => void;
        setEligibility: (value: BrowseFilter) => void;
        setShowNotesAndRates: (value: boolean) => void;
        setSendBookerPitchDigest: (value: boolean) => void;
        setFileUrl: (url: string | undefined) => void;
    },
) {
    reset({
        campaignName: loaded.campaignName || "",
        details: loaded.details || "",
        deliverables: loaded.deliverables || "",
        soundUrl: loaded.soundUrl || "",
        minPricePerCreator: loaded.minPricePerCreator ?? undefined,
        maxPricePerCreator: loaded.maxPricePerCreator ?? undefined,
        boostInterest: Boolean(loaded.boostInterest),
    });

    setters.setBooker(
        loaded.booker
            ? {
                id: loaded.booker.uid,
                firstName: loaded.booker.firstName,
                lastName: loaded.booker.lastName,
                email: loaded.booker.email,
                company: loaded.booker.company,
                phone: loaded.booker.phone,
                airtableBookerId: loaded.booker.airtableBookerId,
            }
            : undefined,
    );
    setters.setCurrency(loaded.currency);
    setters.setCurrencyLockedFromBooker(false);
    setters.setCampaignType(loaded.campaignType || "Brand");
    setters.setPlatforms(loaded.campaignPlatform || []);
    setters.setEligibility(getBrowseFilterFromEligibility(loaded.eligibility));
    setters.setShowNotesAndRates(loaded.showNotesAndRates !== false);
    setters.setSendBookerPitchDigest(Boolean(loaded.sendBookerPitchDigest));
    setters.setFileUrl(loaded.fileUrl);
}

type BuildCampaignFromFormArgs = {
    mode: CampaignFormMode;
    existing?: Campaign;
    formData: CampaignFormValues;
    authenticatedUser: User | undefined;
    booker: User;
    currency?: Currency;
    campaignType: CampaignType;
    platforms: Platform[];
    eligibility: PitchingEligibility;
    showNotesAndRates: boolean;
    sendBookerPitchDigest: boolean;
    salesTeam: User[];
    basicsLocked: boolean;
    openAfterSave?: boolean;
};

function campaignFormFields(args: BuildCampaignFromFormArgs) {
    const {
        existing,
        formData,
        authenticatedUser,
        currency,
        campaignType,
        platforms,
        eligibility,
        showNotesAndRates,
        sendBookerPitchDigest,
        salesTeam,
        basicsLocked,
    } = args;

    const nextEligibility = normalizeEligibility(eligibility);
    const creatorId = args.mode === "create" ? authenticatedUser?.id : existing?.createdById;
    const bookerId = args.mode === "create" ? args.booker.id : existing?.booker?.uid;
    const campaignTypeForSound = basicsLocked ? existing?.campaignType : campaignType;
    const soundUrl =
        campaignTypeForSound === "Song" && formData.soundUrl?.length
            ? removeParamsFromURL(formData.soundUrl)
            : null;

    return {
        details: formData.details,
        deliverables: formData.deliverables,
        minPricePerCreator: storedPrice(formData.minPricePerCreator),
        maxPricePerCreator: storedPrice(formData.maxPricePerCreator),
        soundUrl,
        eligibility: nextEligibility,
        showNotesAndRates,
        ...(canSendBookerPitchDigest(creatorId, bookerId) ? { sendBookerPitchDigest } : {}),
        boostInterest: Boolean(formData.boostInterest),
        lastUpdateByManager: Boolean(authenticatedUser?.roles?.manager),
        ...(formData.leadSource !== undefined
            ? { leadSource: formData.leadSource?.length ? formData.leadSource : null }
            : {}),
        ...(formData.crmLeadId !== undefined
            ? { crmLeadId: formData.crmLeadId || null }
            : {}),
        ...(formData.salesTeamId !== undefined
            ? {
                salesTeamId: asIdList(formData.salesTeamId),
                airtableSalesTeamId: airtableSalesTeamIdForSave(formData.salesTeamId, salesTeam),
            }
            : {}),
        ...(!basicsLocked
            ? {
                campaignName: formData.campaignName,
                currency: currency ?? null,
                campaignType,
                campaignPlatform: platforms,
            }
            : {}),
    };
}

function buildCreateCampaignFromForm(args: BuildCampaignFromFormArgs) {
    const { authenticatedUser, booker, formData } = args;
    const autoIsTest =
        booker.email === "timothy.noetzel@gmail.com" || import.meta.env.DEV;
    const id = generateNewDocId("campaigns");
    const owned = stripCampaignWriteExtras({
        airtableId: null,
        booker: {
            airtableBookerId: booker.airtableBookerId,
            firstName: booker.firstName,
            lastName: booker.lastName,
            email: booker.email,
            company: booker.company,
            phone: booker.phone,
            uid: booker.id,
        },
        uid: booker.id,
        ...campaignFormFields(args),
        ...(formData.leadSource === undefined ? { leadSource: null } : {}),
        ...(formData.crmLeadId === undefined ? { crmLeadId: null } : {}),
        sendBookingOpp: false,
        sendCampaignReceipt: false,
        ...(autoIsTest ? { isTest: true } : {}),
        ...(authenticatedUser?.id
            ? {
                pitchingOpen: true,
                pitchingOpenChangedAt: new Date(),
                createdById: authenticatedUser.id,
                createdByRole: createdByRoleFromUser(authenticatedUser),
                pitchCount: 0,
            }
            : {}),
    });
    return createCampaignUpdate({
        id,
        ...convertNestedEmptyToNull(owned),
        bookings: null,
    });
}

function buildEditCampaignFromForm(args: BuildCampaignFromFormArgs) {
    const { existing, openAfterSave } = args;
    const owned = stripCampaignWriteExtras({
        ...campaignFormFields(args),
        ...(openAfterSave
            ? {
                pitchingOpen: true,
                ...(existing?.pitchingOpen !== true
                    ? { pitchingOpenChangedAt: new Date() }
                    : {}),
            }
            : {}),
    });
    return createCampaignUpdate(convertNestedEmptyToNull(owned));
}

export function buildCampaignFromForm(args: BuildCampaignFromFormArgs & { mode: "create" }): ReturnType<typeof buildCreateCampaignFromForm>;
export function buildCampaignFromForm(args: BuildCampaignFromFormArgs & { mode: "edit" }): ReturnType<typeof buildEditCampaignFromForm>;
export function buildCampaignFromForm(args: BuildCampaignFromFormArgs) {
    return args.mode === "create"
        ? buildCreateCampaignFromForm(args)
        : buildEditCampaignFromForm(args);
}

export async function countNewlyExcludedPitches(args: {
    campaignId: string;
    previousEligibility?: PitchingEligibility;
    nextEligibility: PitchingEligibility;
    platforms: Platform[];
}) {
    const pitches: Pitch[] = await getPitchesByCampaignId(args.campaignId);
    if (!pitches.length) {
        return 0;
    }

    const talentIds = [...new Set(pitches.map((pitch) => pitch.talentId))];
    const talent = await Promise.all(talentIds.map((id) => getTalentById(id)));
    const talentById = new Map<string, Talent>(talent.map((item) => [item.id, item]));

    return pitches.reduce((count, pitch) => {
        const creator = talentById.get(pitch.talentId);
        if (!creator) {
            return count;
        }
        const matchedBefore = talentMatchesPitchingEligibility(
            creator,
            args.previousEligibility,
            args.platforms,
        );
        const matchesAfter = talentMatchesPitchingEligibility(
            creator,
            args.nextEligibility,
            args.platforms,
        );
        return matchedBefore && !matchesAfter ? count + 1 : count;
    }, 0);
}
